/**
 * One arm of a run: model, effort, and an optional draft prompt that replaces the
 * production prompt for this arm only.
 */
import { Trash } from 'lucide-react';
import Button from '../../../components/Button';
import type { Effort, EvalModel } from '../types/evalTypes';

export interface ArmDraft {
  /** React key; stable while the arm exists. */
  key: string;
  model: string;
  effort: Effort | '';
  /** `null`: use the production prompt. */
  override: string | null;
}

interface ArmEditorProps {
  arm: ArmDraft;
  models: EvalModel[];
  productionModel: string;
  /** `null` until a draft is started; the API sends it only then. */
  productionPrompt: string | null;
  promptLoading: boolean;
  maxPromptChars: number;
  canRemove: boolean;
  disabled: boolean;
  onChange: (change: Partial<ArmDraft>) => void;
  /** Starting a draft fetches the production prompt first, so the parent sets `override`. */
  onToggleDraft: (enabled: boolean) => void;
  onRemove: () => void;
}

const fieldClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-100';

export const ArmEditor = ({
  arm,
  models,
  productionModel,
  productionPrompt,
  promptLoading,
  maxPromptChars,
  canRemove,
  disabled,
  onChange,
  onToggleDraft,
  onRemove,
}: ArmEditorProps) => {
  const efforts = models.find((model) => model.id === arm.model)?.efforts ?? [];
  const isProduction = arm.model === productionModel && arm.effort === '' && arm.override === null;

  return (
    <div className="border border-gray-200 rounded-lg p-3 bg-gray-50 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-[1fr_10rem_auto] gap-3 items-end">
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">Model</span>
          <select
            value={arm.model}
            disabled={disabled}
            onChange={(event) => {
              const nextEfforts = models.find((model) => model.id === event.target.value)?.efforts ?? [];
              onChange({ model: event.target.value, effort: arm.effort && nextEfforts.includes(arm.effort) ? arm.effort : '' });
            }}
            className={fieldClass}
          >
            {models.filter((model) => !model.judgeOnly).map((model) => (
              <option key={model.id} value={model.id}>
                {model.label}
                {model.id === productionModel ? ' (production)' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-gray-600 mb-1">Effort</span>
          <select
            value={arm.effort}
            disabled={disabled || efforts.length === 0}
            onChange={(event) => onChange({ effort: event.target.value as Effort | '' })}
            className={fieldClass}
          >
            <option value="">{efforts.length === 0 ? 'Not supported' : 'Model default'}</option>
            {efforts.map((effort) => (
              <option key={effort} value={effort}>
                {effort}
              </option>
            ))}
          </select>
        </label>
        <Button
          variant="ghost"
          size="sm"
          icon={<Trash size={14} />}
          disabled={disabled || !canRemove}
          onClick={onRemove}
          title="Remove arm"
          aria-label="Remove arm"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={arm.override !== null}
            disabled={disabled || promptLoading}
            onChange={(event) => onToggleDraft(event.target.checked)}
          />
          Draft prompt
        </label>
        {promptLoading && <span className="text-xs text-gray-500">Loading the production prompt…</span>}
        {isProduction && <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">Production setup</span>}
      </div>

      {arm.override !== null && (
        <div>
          <textarea
            value={arm.override}
            disabled={disabled}
            onChange={(event) => onChange({ override: event.target.value })}
            rows={12}
            spellCheck={false}
            className={`${fieldClass} font-mono text-xs resize-y`}
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>Replaces the production prompt for this arm only. {'{{placeholders}}'} are filled the same way.</span>
            <span className={arm.override.length > maxPromptChars ? 'text-red-600 font-medium' : ''}>
              {arm.override.length.toLocaleString()} / {maxPromptChars.toLocaleString()}
            </span>
          </div>
          <button
            type="button"
            disabled={disabled || productionPrompt === null || arm.override === productionPrompt}
            onClick={() => productionPrompt !== null && onChange({ override: productionPrompt })}
            className="text-xs text-blue-600 hover:underline disabled:text-gray-400 disabled:no-underline mt-1"
          >
            Reset to the production prompt
          </button>
        </div>
      )}
    </div>
  );
};
